import type {ITestheftAPI} from './Interfaces';

/**
 * Die abstrakte Klasse `Utilities` bietet statische, portalweit nutzbare Hilfsmethoden an.
 */
export default abstract class Utilities {
    /**
     * Beschränkt die Eingabe in einem Textfeld auf erlaubte Zeichen.
     * Unerlaubte Zeichen werden entfernt. Optional können Zeichen in Großbuchstaben
     * umgewandelt und Leerzeichen ersetzt werden.
     *
     * @param target - Das `HTMLInputElement`, auf das die Einschränkung angewendet wird.
     * @param charList - Eine Zeichenkette im Stil einer regulären Ausdruck-Zeichenklasse (z.B. 'a-zA-Z0-9'), die die erlaubten Zeichen definiert.
     * @param upper - Wenn `true`, wird die gesamte Eingabe in Großbuchstaben umgewandelt.
     * @param spacebar - Wenn `true` und `spaceReplace` ein Wert zugewiesen ist, werden Leerzeichen ersetzt.
     * @param spaceReplace - Das Zeichen, durch das Leerzeichen ersetzt werden sollen.
     */
    public static forceKeys(target: HTMLInputElement, charList: string, upper: boolean, spacebar: boolean, spaceReplace: string = null): void {
        if (upper === true) target.value = target.value.toUpperCase();
        if (spacebar === true && spaceReplace !== null) target.value = target.value.replace(" ", spaceReplace);
        let regEx = new RegExp(`[^${charList}]`, 'gi');
        target.value = target.value.replace(regEx, '');
    }

    /**
     * Erstellt einen MYSQL-konformen `DATETIME`-String aus der aktuellen Systemzeit.
     * @returns Ein String im Format 'YYYY-MM-DD HH:MM:SS'.
     */
    public static getDatetime() {
        return new Date((new Date).getTime() - ((new Date).getTimezoneOffset() * 60000)).toISOString().substring(0, 20).replace('T', ' ');
    }

    /**
     * Zeigt eine temporäre Nachrichten-Popup-Benachrichtigung am unteren Rand des Bildschirms an.
     *
     * @param nachricht - Die anzuzeigende Nachricht.
     * @param titel - Optionaler Titel für das Popup.
     * @param dauer - Anzeigedauer in Millisekunden, bevor das Popup ausgeblendet wird.
     */
    public static showMessagePopup(nachricht: string, titel: string = null, dauer: number = 6000): void {
        let messagePopupId = `message-popup-${Math.round(Math.random() * 1000).toString()}`;
        let messagePopupHTML = `
        <div id="${messagePopupId}" class="message-popup">
            ${(titel !== null) ? `<h1>${titel}</h1>` : ``}
            <p>${nachricht}</p>
        </div>
        `;
        $('body').append(messagePopupHTML);
        setTimeout(() => {
            $(`#${messagePopupId}`).removeClass('hidden');
            setTimeout(() => {
                $(`#${messagePopupId}`).addClass('hidden');
                setTimeout(() => {
                    $(`#${messagePopupId}`).remove();
                }, 1000);
            }, dauer);
        }, 250);
    }


    /**
     * Setzt ein Cookie im Browser.
     * Basiert auf der Implementierung von:
     *
     * @param name - Der Name des Cookies.
     * @param value - Der Wert des Cookies.
     * @param minutes - Die Gültigkeitsdauer des Cookies in Minuten.
     */
    public static setCookie(name: string, value: string, minutes: number): void {
        let expires = '';
        if (minutes) {
            let date = new Date();
            date.setTime(date.getTime() + (minutes * 60 * 1000));

            expires = `; expires=${date.toUTCString()}; SameSite=Strict; Secure`;
        }
        document.cookie = `${name}=${(value || '')}${expires}; path=/`;
    }


    /**
     * Liest den Wert eines Cookies aus.
     * Basiert auf der Implementierung von: https://stackoverflow.com/a/14573223
     *
     * @param name - Der Name des zu lesenden Cookies.
     * @returns Den Wert des Cookies oder `null`, falls es nicht gefunden wurde.
     */
    public static getCookie(name: string): string | null {
        let nameEQ = `${name}=`;
        let ca = document.cookie.split(';');
        for (let i = 0; i < ca.length; i++) {
            let c = ca[i];
            while (c.charAt(0) === ' ') c = c.substring(1, c.length);
            if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
        }
        return null;
    }


    /**
     * Löscht ein Cookie, indem sein Ablaufdatum in die Vergangenheit gesetzt wird.
     *
     * @param name - Der Name des zu löschenden Cookies.
     */
    public static eraseCookie(name: string): void {
        document.cookie = `${name}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT; SameSite=None; Secure`;
    }


    /**
     * Führt eine summenerhaltende Rundung auf einem Array von Zahlen durch.
     * Diese Methode ist nützlich, um beispielsweise Prozentwerte so zu runden,
     * dass ihre Summe exakt 100 ergibt. Rundungsfehler werden auf die nachfolgenden
     * Werte übertragen und verrechnet.
     *
     * @param ungerundetArr - Ein Array von Gleitkommazahlen.
     * @returns Ein Array von gerundeten Ganzzahlen, deren Summe der Summe der Originalzahlen entspricht (gerundet).
     */
    public static summenerhaltendRunden(ungerundetArr: number[]): number[] {
        let gerundetArr: number[] = [];
        let delta = 0;

        for (let i = 0; i < ungerundetArr.length; i++) {
            // SICHERHEITS-CHECK: Wenn das Element leer (null/undefined) oder keine Zahl ist, nimm 0
            let aktuellerWert = ungerundetArr[i];
            if (aktuellerWert === undefined || aktuellerWert === null || isNaN(aktuellerWert)) {
                aktuellerWert = 0;
            }
            let neuProzZahl = aktuellerWert + delta;
            let gerundetNeuProzZahl = Math.round(neuProzZahl);
            if (gerundetNeuProzZahl < 0) {
                gerundetNeuProzZahl = 0;
            }
            delta = neuProzZahl - gerundetNeuProzZahl;
            gerundetArr.push(Math.abs(gerundetNeuProzZahl));
        }

        if (gerundetArr.length < 1) return [];

        return gerundetArr;
    }


    /**
     * Wandelt ein Fachkürzel oder ein Array von Kürzeln in den vollständigen Fachnamen um.
     *
     * @param fachKuerzel - Ein einzelnes Fachkürzel (z.B. 'de') oder ein Array von Kürzeln.
     * @returns Den vollständigen Fachnamen (z.B. 'Deutsch') oder eine formatierte Aufzählung bei einem Array.
     * Wirft einen Fehler, wenn ein Kürzel unbekannt ist.
     */
    public static printFachLang(fachKuerzel: string | string[]) {
        if (Array.isArray(fachKuerzel) === false) {
            if (fachKuerzel === 'ma')
                return 'Mathematik';
            if (fachKuerzel === 'de')
                return 'Deutsch';
            if (fachKuerzel === 'en')
                return 'Englisch';
            if (fachKuerzel === 'fr')
                return 'Französisch';
            if (fachKuerzel === null)
                return '';
        } else {
            fachKuerzel = fachKuerzel.filter(k => ['ma', 'de', 'en', 'fr'].includes(k));

            if (fachKuerzel.length === 0)
                return `keinem Fach`;

            let aufzaehlung = ``;
            for (let i = 0; i < fachKuerzel.length; i++) {
                aufzaehlung += this.printFachLang(fachKuerzel[i]);
                if (i < fachKuerzel.length - 2) {
                    aufzaehlung += `, `;
                }
                if (i === fachKuerzel.length - 2) {
                    aufzaehlung += ` und `;
                }
            }
            return aufzaehlung;
        }

        throw 'Fachkürzel nicht bekannt';
    }


    /**
     * Wandelt ein Modulkürzel in den vollständigen Modulnamen um.
     *
     * @param kuerzel - Das Modulkürzel (z.B. 'BM' oder 'EM').
     * @returns Den vollständigen Modulnamen ('Basismodul' oder 'Ergänzungsmodul').
     * Wirft einen Fehler, wenn das Kürzel unbekannt ist.
     */
    public static printModulLang(kuerzel: string): string {
        if (kuerzel.toUpperCase() === 'BM') {
            return 'Basismodul';
        }
        if (kuerzel.toUpperCase() === 'EM') {
            return 'Ergänzungsmodul';
        }
        throw new Error('Modulkürzel nicht bekannt');
    }

    /** Gibt die Aufgabenposition in der für Lernstand 5 verwendeten Form zurück */
    public static printVera5Aufgabenposition(aufgabePosition: number, itemPosition: string | number, anzahlItems: number): string {
        const unteraufgabe = (anzahlItems > 1) ? `.${itemPosition}` : '';
        return `Aufgabe ${aufgabePosition}${unteraufgabe}`;
    }

    /** Ermittelt die modulübergreifende Anzeigenummer einer Lernstand-5-Aufgabe */
    public static getVera5Aufgabenposition(testheft: ITestheftAPI | null, aufgabeId: number, fallbackPosition: number): number {
        if (testheft === null)
            return fallbackPosition;

        const aufgabeIndex = testheft.module
            .flatMap(modul => modul.aufgaben)
            .findIndex(aufgabe => aufgabe.aufgabe_id === aufgabeId);

        return (aufgabeIndex === -1) ? fallbackPosition : aufgabeIndex + 1;
    }

    private static printNwNiveaustufe(iqbItemId?: string): string {
        // Matchingtabelle für Spezialkompetenzstufen NRW 2026 hardgecoded - Ticket 2921
        const nwIqbItemIdsByNiveaustufe: Record<string, string[]> = {
            '0': [
                'CE2400101', 'CE2403601', 'CE2403604', 'CO2407001', 'CO2407103', 'CO2407104', 'CO2407105', 'DH03803',
                'DH03805', 'DL07201', 'DL11201', 'DL11203', 'DL12201', 'DO13702', 'EL77801', 'EL79501',
                'EL83903', 'ER81107', 'M5650501', 'M5670101', 'M5731101',
            ],
            '1': [
                'CE2401802', 'CE2402608', 'CE2403104', 'CE2403105', 'CE2403107', 'CE2403605', 'CE2404101', 'CO2205201',
                'CO2400302', 'CO2400501', 'CO2400701', 'CO2400702', 'CO2404003', 'CO2406701', 'CO2407002', 'CO2407003',
                'CO2407004', 'CO2407101', 'CO2407102', 'CO2407701', 'CO2407702', 'CO2407704', 'CO2407802', 'CO2407803',
                'DH03806', 'DH03810', 'DH03903', 'DH04204', 'DH04214', 'DH04216', 'DL07204', 'DL07207',
                'DL07211', 'DL11401', 'DL11404', 'DL11419', 'DL12002', 'DL12206', 'DO03404', 'DO13701',
                'DO13707', 'DO13708', 'DO13718', 'DO13719', 'DO14401', 'DO14410', 'DO14809', 'DO15107',
                'DO15110', 'DO15120', 'DO15122', 'EL506r01', 'EL541r01', 'EL541r02', 'EL541r03', 'EL541r04',
                'EL541r06', 'EL712r03', 'EL73801', 'EL73802', 'EL73803', 'EL73804', 'EL77805', 'EL79504',
                'EL79706', 'EL81502', 'EL83501', 'EL83503', 'EL83901', 'EL83902', 'EL83904', 'EL83906',
                'ER555r02', 'ER555r03', 'ER645r03', 'ER70001', 'ER70002', 'ER70003', 'ER70005', 'ER70006',
                'ER71301', 'ER71303', 'ER75901', 'ER80306', 'ER80903', 'ER81203', 'ER83301',
            ],
            '1a': [
                'M1680701', 'M1710701', 'M2660201', 'M2710401', 'M4700301', 'M4720101', 'M4722101', 'M5660101',
                'M5673101',
            ],
            '1b': [
                'M1701302', 'M1711301', 'M1720802', 'M1721601', 'M1721901', 'M1731701', 'M2640601', 'M2701502',
                'M2731901', 'M2732002', 'M3671401', 'M3700401', 'M3710401', 'M3720602', 'M3722301', 'M4643503',
                'M4720301', 'M4720801', 'M4720901', 'M4721201', 'M4722002', 'M4723101', 'M4723502', 'M5620902',
                'M5662101', 'M5701301', 'M5701302', 'M5701401', 'M5710701', 'M5711901', 'M5713202', 'M5730901',
            ],
            '2': [
                'CE2200901', 'CE2200902', 'CE2200905', 'CE2303801', 'CE2303802', 'CE2303803', 'CE2303804', 'CE2303805',
                'CE2303806', 'CE2304301', 'CE2305701', 'CE2310301', 'CE2400102', 'CE2400103', 'CE2400104', 'CE2400105',
                'CE2400106', 'CE2402601', 'CE2402602', 'CE2402603', 'CE2402604', 'CE2402606', 'CE2403101', 'CE2403102',
                'CE2403103', 'CE2403106', 'CE2403501', 'CE2403602', 'CE2403603', 'CE2404102', 'CE2404103', 'CE2404401',
                'CE2404403', 'CE2404404', 'CE2405701', 'CE2406201', 'CO2302601', 'CO2302602', 'CO2302603', 'CO2302604',
                'CO2305201', 'CO2305202', 'CO2305203', 'CO2400301', 'CO2400703', 'CO2401201', 'CO2401202', 'CO2402801',
                'CO2402802', 'CO2402804', 'CO2403301', 'CO2403302', 'CO2404001', 'CO2404004', 'CO2404802', 'CO2406702',
                'CO2407106', 'CO2407703', 'CO2407801', 'DH03801', 'DH03809', 'DH03813', 'DH03814', 'DH03911',
                'DH04105', 'DH04108', 'DH04205', 'DH04210', 'DH04312', 'DL07202', 'DL07205', 'DL07206',
                'DL07209', 'DL08001', 'DL08003', 'DL08007', 'DL08502', 'DL08503', 'DL08504', 'DL08506',
                'DL09505', 'DL11204', 'DL11207', 'DL11212', 'DL11218', 'DL11402', 'DL11403', 'DL11406',
                'DL11407', 'DL11417', 'DL12001', 'DL12003', 'DL12007', 'DL12008', 'DL12010', 'DL12202',
                'DL12203', 'DL12204', 'DL12208', 'DO03402', 'DO03403', 'DO13605', 'DO13608', 'DO13703',
                'DO13709', 'DO13711', 'DO13713', 'DO13714', 'DO13715', 'DO13724', 'DO14403', 'DO14406',
                'DO14417', 'DO14602', 'DO14605', 'DO14807', 'DO15108', 'DO15109', 'DO15111', 'DO15117',
                'DO15125', 'EL506r03', 'EL506r07', 'EL541r05', 'EL589r01', 'EL589r02', 'EL589r03', 'EL648v301',
                'EL648v303', 'EL710r03', 'EL710r04', 'EL712r01', 'EL712r02', 'EL736v303', 'EL736v304', 'EL736v305',
                'EL736v306', 'EL736v307', 'EL77101', 'EL77102', 'EL77106', 'EL77402', 'EL77405', 'EL77802',
                'EL77803', 'EL77804', 'EL781v101', 'EL781v102', 'EL781v103', 'EL78704', 'EL78706', 'EL78707',
                'EL79502', 'EL79503', 'EL79702', 'EL81403', 'EL81404', 'EL81405', 'EL81503', 'EL81505',
                'EL81506', 'EL82001', 'EL82005', 'EL83407', 'EL83504', 'EL83905', 'ER555r01', 'ER555r04',
                'ER645r05', 'ER645r06', 'ER663v301', 'ER663v302', 'ER663v304', 'ER66902', 'ER66904', 'ER70004',
                'ER70007', 'ER70602', 'ER70603', 'ER71302', 'ER71304', 'ER71802', 'ER71803', 'ER73502',
                'ER73503', 'ER73504', 'ER74301', 'ER74302', 'ER74303', 'ER74305', 'ER74306', 'ER74601',
                'ER75202', 'ER75701', 'ER75902', 'ER76302', 'ER76401', 'ER76402', 'ER76404', 'ER76405',
                'ER78101', 'ER79101', 'ER80301', 'ER80803', 'ER80901', 'ER80905', 'ER81101', 'ER81102',
                'ER81103', 'ER81104', 'ER81105', 'ER81106', 'ER81108', 'ER81201', 'ER81202', 'ER81701',
                'ER82904', 'ER83302', 'ER83303', 'ER83305', 'M1670402', 'M1713901', 'M1721201', 'M1721202',
                'M2401102', 'M2645401', 'M2650501', 'M2673601', 'M2701802', 'M2702801', 'M4621801', 'M4621901',
                'M4660801', 'M4690901', 'M4710101', 'M4720102', 'M4720302', 'M4721301', 'M4721302', 'M4721501',
                'M4722102', 'M4723501', 'M4723701', 'M5660102', 'M5662601', 'M5673102', 'M5690201', 'M5701402',
                'M5713602', 'M5720201', 'M5720401', 'M5730102', 'M5730902', 'M5732501', 'M5732601',
            ],
            '3': [
                'CE2200903', 'CE2200904', 'CE2206801', 'CE2206802', 'CE2206803', 'CE2311101', 'CE2401701', 'CE2401702',
                'CE2401801', 'CE2401803', 'CE2402605', 'CE2402607', 'CE2404104', 'CE2404402', 'CE2404405', 'CE2406403',
                'CO2305204', 'CO2402803', 'CO2402805', 'CO2403001', 'CO2403303', 'CO2403501', 'CO2404002', 'CO2405401',
                'CO2406703', 'CO2407804', 'DH03802', 'DH03804', 'DH03811', 'DH03812', 'DH03905', 'DH03906',
                'DH03909', 'DH03912', 'DH03917', 'DH03918', 'DH03920', 'DH03924', 'DH04101', 'DH04103',
                'DH04104', 'DH04107', 'DH04109', 'DH04112', 'DH04114', 'DH04117', 'DH04118', 'DH04119',
                'DH04120', 'DH04121', 'DH04202', 'DH04203', 'DH04206', 'DH04207', 'DH04209', 'DH04211',
                'DH04212', 'DH04213', 'DH04217', 'DH04304', 'DH04306', 'DH04307', 'DH04309', 'DH04310',
                'DH04313', 'DL07203', 'DL07212', 'DL07214', 'DL08004', 'DL08005', 'DL08008', 'DL08011',
                'DL08012', 'DL08016', 'DL08505', 'DL09502', 'DL11211', 'DL11215', 'DL11217', 'DL11411',
                'DL11414', 'DL12006', 'DL12011', 'DL12018', 'DL12028', 'DL12210', 'DL12211', 'DL12212',
                'DO03405', 'DO13402', 'DO13403', 'DO13404', 'DO13405', 'DO13502', 'DO13504', 'DO13507',
                'DO13607', 'DO13704', 'DO13712', 'DO13717', 'DO13720', 'DO13722', 'DO13737', 'DO13802',
                'DO13803', 'DO14404', 'DO14405', 'DO14408', 'DO14409', 'DO14411', 'DO14412', 'DO14414',
                'DO14416', 'DO14418', 'DO14421', 'DO14422', 'DO14425', 'DO14803', 'DO14805', 'DO15101',
                'DO15102', 'DO15103', 'DO15104', 'DO15112', 'DO15113', 'DO15114', 'DO15115', 'DO15116',
                'DO15118', 'DO15119', 'DO15126', 'DO15133', 'DO15135', 'EL506r02', 'EL506r04', 'EL506r05',
                'EL506r06', 'EL589r04', 'EL589r05', 'EL648v302', 'EL648v305', 'EL648v306', 'EL696r201', 'EL710r01',
                'EL710r02', 'EL723v302', 'EL723v304', 'EL723v305', 'EL723v307', 'EL727v302', 'EL727v303', 'EL727v304',
                'EL736v301', 'EL736v302', 'EL77103', 'EL77104', 'EL77105', 'EL77401', 'EL77404', 'EL77406',
                'EL781v104', 'EL78701', 'EL78702', 'EL78703', 'EL78705', 'EL79701', 'EL79703', 'EL79704',
                'EL79705', 'EL80701', 'EL81401', 'EL81402', 'EL81504', 'EL82002', 'EL82003', 'EL82004',
                'EL833v1', 'EL83402', 'EL83406', 'EL83502', 'EL87601', 'EL87603', 'EL87604', 'EL87606',
                'ER541r03', 'ER541r05', 'ER555r05', 'ER584r01', 'ER584r02', 'ER584r05', 'ER645r01', 'ER645r02',
                'ER645r04', 'ER651r01', 'ER663v303', 'ER663v305', 'ER66901', 'ER66903', 'ER66905', 'ER66906',
                'ER66907', 'ER694rv201', 'ER70601', 'ER70604', 'ER71801', 'ER71902', 'ER71903', 'ER71904',
                'ER73101', 'ER73501', 'ER74101', 'ER74304', 'ER74603', 'ER74605', 'ER75201', 'ER75203',
                'ER75204', 'ER75205', 'ER75903', 'ER75904', 'ER75905', 'ER76301', 'ER76303', 'ER76304',
                'ER76305', 'ER76403', 'ER76406', 'ER776v302', 'ER776v303', 'ER776v304', 'ER776v305', 'ER78402',
                'ER78403', 'ER78404', 'ER78405', 'ER78406', 'ER79102', 'ER79103', 'ER79104', 'ER80302',
                'ER80303', 'ER80304', 'ER80305', 'ER80307', 'ER80804', 'ER80906', 'ER82601', 'ER82603',
                'ER82604', 'ER82901', 'ER82902', 'ER82903', 'ER83304', 'M1402201', 'M1662301', 'M1663801',
                'M1663802', 'M1700601', 'M1714501', 'M1714502', 'M1720101', 'M2620402', 'M2674001', 'M2700302',
                'M2702501', 'M2710301', 'M2720201', 'M3621401', 'M3662201', 'M4700302', 'M4701101', 'M4720201',
                'M4720303', 'M4720701', 'M4722103', 'M4722601', 'M4723102', 'M4723303', 'M5632002', 'M5700301',
                'M5701201', 'M5701901', 'M5701902', 'M5720901', 'M5721302', 'M5721303', 'M5730103', 'M5731102',
            ],
            '4': [
                'CE2311102', 'CE2311103', 'CE2401804', 'CE2401805', 'CE2406401', 'CE2406402', 'CE2406404', 'CE2406405',
                'CE2406406', 'CO2402701', 'CO2402702', 'CO2402703', 'CO2404801', 'CO2404803', 'CO2404804', 'DH03807',
                'DH03901', 'DH03907', 'DH03913', 'DH03914', 'DH03921', 'DH03922', 'DH04110', 'DH04115',
                'DH04116', 'DH04215', 'DH04218', 'DH04301', 'DH04302', 'DH04308', 'DL07208', 'DL07210',
                'DL07215', 'DL08002', 'DL08015', 'DL08017', 'DL08509', 'DL08512', 'DL08513', 'DL08516',
                'DL09503', 'DL09507', 'DL11205', 'DL11209', 'DL11213', 'DL11214', 'DL11405', 'DL11408',
                'DL11413', 'DL11420', 'DL12004', 'DL12009', 'DL12013', 'DL12014', 'DL12016', 'DL12017',
                'DL12218', 'DO03401', 'DO09405', 'DO09408', 'DO13401', 'DO13505', 'DO13506', 'DO13508',
                'DO13509', 'DO13511', 'DO13603', 'DO13706', 'DO13721', 'DO13804', 'DO13805', 'DO13806',
                'DO13807', 'DO13811', 'DO14302', 'DO14303', 'DO14407', 'DO14420', 'DO14423', 'DO14603',
                'DO14604', 'DO14606', 'DO14801', 'DO14804', 'DO14902', 'DO15105', 'DO15124', 'DO15127',
                'DO15132', 'DO15134', 'DO15136', 'DO15137', 'DO15138', 'DO15139', 'EL648v304', 'EL696r101',
                'EL723v301', 'EL723v303', 'EL723v306', 'EL727v301', 'EL77403', 'EL81501', 'EL81507', 'EL83401',
                'EL83403', 'EL83404', 'EL83405', 'EL83602', 'EL83603', 'EL83604', 'EL83605', 'EL83801',
                'EL83802', 'EL83803', 'EL83804', 'EL84301', 'EL87602', 'EL87605', 'ER541r01', 'ER541r02',
                'ER541r04', 'ER584r03', 'ER584r04', 'ER71901', 'ER73102', 'ER73103', 'ER73104', 'ER73105',
                'ER73106', 'ER74602', 'ER74604', 'ER75501', 'ER776v301', 'ER78401', 'ER78901', 'ER78903',
                'ER78904', 'ER78905', 'ER78907', 'ER79105', 'ER80801', 'ER80802', 'ER80805', 'ER80806',
                'ER80807', 'ER80808', 'ER80902', 'ER80904', 'ER82602', 'ER82605', 'ER82606', 'ER82607',
                'ER82608', 'M1663702', 'M1663703', 'M2700701', 'M2703501', 'M2703701', 'M2720202', 'M2720602',
                'M2732901', 'M4701102', 'M4701103', 'M4723201', 'M4730905', 'M5673104', 'M5713302', 'M5720902',
                'M5730802', 'M5732802',
            ],
            '5': [
                'DH03815', 'DH03902', 'DH03904', 'DH03915', 'DH03916', 'DH04111', 'DH04305', 'DL07216',
                'DL08006', 'DL08009', 'DL08013', 'DL08014', 'DL08018', 'DL08019', 'DL08507', 'DL08508',
                'DL09504', 'DL09506', 'DL09508', 'DL09509', 'DL09511', 'DL09512', 'DL09513', 'DL11210',
                'DL11409', 'DL11410', 'DL11415', 'DL11416', 'DL11418', 'DL11421', 'DL12005', 'DL12020',
                'DL12021', 'DL12026', 'DL12027', 'DL12207', 'DL12209', 'DL12213', 'DL12215', 'DO09401',
                'DO09402', 'DO09403', 'DO09404', 'DO09407', 'DO09409', 'DO09410', 'DO09411', 'DO09412',
                'DO13501', 'DO13503', 'DO13510', 'DO13601', 'DO13604', 'DO13705', 'DO13716', 'DO13723',
                'DO13725', 'DO13726', 'DO13727', 'DO13728', 'DO13729', 'DO13730', 'DO13731', 'DO13733',
                'DO13735', 'DO13801', 'DO13808', 'DO13810', 'DO13812', 'DO13813', 'DO14301', 'DO14304',
                'DO14419', 'DO14424', 'DO14426', 'DO14427', 'DO14601', 'DO14607', 'DO14608', 'DO14806',
                'DO14808', 'DO14901', 'DO15128', 'EL83601', 'EL87607', 'ER71905', 'ER78902', 'ER78906',
                'M1700602', 'M1722103', 'M2674004', 'M2691301', 'M2702101', 'M2720203', 'M3700402', 'M4601302',
                'M4643702', 'M4711602', 'M4720902', 'M4723202', 'M4723702', 'M5701202', 'M5730801',
            ],
        };

        const normalisierteIqbItemId = iqbItemId?.trim() ?? '';
        if (normalisierteIqbItemId === '')
            return '';

        for (const [niveaustufe, iqbItemIds] of Object.entries(nwIqbItemIdsByNiveaustufe)) {
            if (iqbItemIds.includes(normalisierteIqbItemId) && niveaustufe === '0')
                return 'k.h.N.';

            if (iqbItemIds.includes(normalisierteIqbItemId))
                return `KS ${niveaustufe}`;
        }

        return '';
    }


    /**
     * Wandelt die importierte numerische Niveaustufe zurück in die ursprüngliche Darstellungsform.
     * Für Englisch und Französisch werden GER-Stufen verwendet, sonst Kompetenzstufen.
     *
     * @param niveaustufe - Die beim Import gespeicherte numerische Stufe, `null` wird als leerer String ausgegeben
     * @param fach - Das Fachkürzel, z.B. 'de', 'en' oder 'fr'
     * @param spalte - Zielspalte der Rückübersetzung: Originalstufe oder Bezeichnung
     * @param iqbItemId - Optionale IQB-Item-ID für länderspezifische Übergangsmappings
     * @param land - Optionales Landeskürzel für länderspezifische Übergangsmappings
     * @returns Die zurückübersetzte Niveaustufe oder ein leerer String bei unbekannten Werten
     */
    public static printNiveaustufe(niveaustufe: number | null, fach: string, spalte: 'Niveaustufe' | 'NiveaustufeBez' = 'Niveaustufe', iqbItemId?: string, land?: string): string {
        if (land === 'nw' && iqbItemId !== undefined) {
            return this.printNwNiveaustufe(iqbItemId);
        }

        if (niveaustufe === null) {
            return '';
        }

        const stufe = niveaustufe;
        const fachKuerzel = fach.toLowerCase();

        if (fachKuerzel === 'en' || fachKuerzel === 'fr') {
            const gerStufen: Record<number, string> = {
                1: 'A1.1',
                2: 'A1.2',
                3: 'A2.1',
                4: 'A2.2',
                5: 'B1.1',
                6: 'B1.2',
                7: 'B2.1',
                8: 'B2.2',
                9: 'C1.1',
            };
            return gerStufen[stufe] ?? '';
        }

        const niveauStufen: Record<number, string> = {
            1: '1a',
            2: '1b',
            3: '2',
            4: '3',
            5: '4',
            6: '5',
        };

        const niveauStufenBez: Record<number, string> = {
            1: 'KSIa',
            2: 'KSIb',
            3: 'KSII',
            4: 'KSIII',
            5: 'KSIV',
            6: 'KSV',
        };

        if (spalte === 'NiveaustufeBez') {
            return niveauStufenBez[stufe] ?? '';
        }

        return niveauStufen[stufe] ?? '';
    }


    /**
     * Gibt die Bezeichnung der Schwierigkeitsstufe für ein Fach zurück.
     * Für die meisten Fächer wird eine numerische Stufe in einen Buchstaben-Code (A, B, C) umgewandelt.
     *
     * @param fach - Das Fachkürzel.
     * @param schwierigkeit - Die numerische Schwierigkeitsstufe (1, 2 oder 3).
     * @returns Die Bezeichnung der Schwierigkeitsstufe (z.B. 'A') oder die ursprüngliche Nummer.
     */
    public static printSchwierigkeit(fach: string, schwierigkeit: number): string | number {
        if (fach === 'fr') {
            return schwierigkeit;
        }
        switch (schwierigkeit) {
            case 1:
                return 'A';
            case 2:
                return 'B';
            case 3:
                return 'C';
        }
        return '';
    }


    /**
     * Erstellt eine tiefe Kopie eines Objekts durch Serialisierung und Deserialisierung mit JSON.
     * Diese Methode stellt sicher, dass keine Referenzen zum Originalobjekt bestehen bleiben.
     * Achtung: Funktioniert nicht für Objekte mit Funktionen, `undefined`, `Symbolen` oder Zyklen.
     *
     * @param obj - Das zu klonende Objekt.
     * @returns Eine tiefe Kopie des Objekts oder `undefined` bei einem Fehler.
     */
    public static cloneObject<T>(obj: T): T | undefined {
        try {
            return JSON.parse(JSON.stringify(obj)) as T;
        } catch {
            return undefined;
        }
    }

    /**
     * Entfernt alle Event-Listener von einem DOM-Element, indem es durch einen Klon ersetzt wird.
     *
     * @param element - Das Element, von dem die Event-Listener entfernt werden sollen.
     * @returns Der neue Klon des Elements, der die alten Listener nicht mehr enthält.
     */
    public static resetEventListeners<T extends HTMLElement>(element: T): T {
        const clone = element.cloneNode(true) as T;
        if (element.parentNode) {
            element.parentNode.replaceChild(clone, element);
        }
        return clone;
    }


    /**
     * Komprimiert einen String mittels GZIP und kodiert das Ergebnis als base64.
     * Die Funktion ist nützlich, um große Datenmengen clientseitig zu verkleinern,
     * bevor sie an den Server gesendet werden.
     *
     * @param str - Der zu komprimierende String.
     * @returns Ein Promise, das bei Erfolg den base64-kodierten, komprimierten String enthält.
     * Bei einem Fehler (z.B. in älteren Browsern ohne CompressionStream API) wird der
     * ursprüngliche String zurückgegeben.
     */
    public static async compressStringWithGzip(str: string): Promise<string> {
        try {
            const textEncoder = new TextEncoder();
            const bytes = textEncoder.encode(str);

            const cs = new CompressionStream('gzip');
            const writer: WritableStreamDefaultWriter = cs.writable.getWriter();
            writer.write(bytes);
            writer.close();

            const compressedStream = cs.readable;
            const compressedData = await new Response(compressedStream).arrayBuffer();

            return btoa(String.fromCharCode(...new Uint8Array(compressedData)));
        } catch {
            return str;
        }
    }

    /**
     * Dekomprimiert einen GZIP-komprimierten und base64-kodierten String.
     *
     * @param compressedStr - Der komprimierte String.
     * @returns Ein Promise, das den dekomprimierten Originalstring enthält.
     * Wenn der String nicht dekomprimiert werden kann (z.B. weil er nicht komprimiert war),
     * wird der ursprüngliche String zurückgegeben.
     */
    public static async decompressStringWithGzip(compressedStr: string): Promise<string> {
        try {
            const binaryStr = atob(compressedStr);
            const bytes = new Uint8Array(binaryStr.length);
            for (let i = 0; i < binaryStr.length; i++) {
                bytes[i] = binaryStr.charCodeAt(i);
            }

            const ds = new DecompressionStream('gzip');
            const writer = ds.writable.getWriter();
            writer.write(bytes);
            writer.close();

            const decompressedStream = ds.readable;
            const decompressedData = await new Response(decompressedStream).arrayBuffer();

            const textDecoder = new TextDecoder();
            return textDecoder.decode(decompressedData);
        } catch {
            return compressedStr;
        }
    }


    /**
     * Pausiert die Ausführung für eine angegebene Anzahl von Millisekunden.
     * Nützlich in `async` Funktionen, um auf etwas zu warten.
     *
     * @param ms - Die Anzahl der Millisekunden, die gewartet werden soll.
     * @returns Ein Promise, das nach der angegebenen Verzögerung aufgelöst wird.
     */
    public static async delay(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    /**
     * Wandelt den ersten Buchstaben eines Strings in einen Großbuchstaben um.
     *
     * @param val - Der zu konvertierende String.
     * @returns Der String mit einem großen Anfangsbuchstaben.
     */
    public static capitalize(val: string): string {
        return String(val).charAt(0).toUpperCase() + String(val).slice(1);
    }


    /**
     * Analysiert den Host der aktuellen URL und zerlegt ihn in seine Bestandteile.
     * Unterscheidet zwischen Subdomain, Domain, Host, Port und gibt eine kombinierte
     * Version ohne Subdomain zurück. Erkennt auch lokale Entwicklungsdomains.
     *
     * @returns Ein Objekt mit den Teilen des Hosts: `subdomain`, `domain`, `host`, `port`, `noSubdomain`.
     * Gibt ein Objekt mit `null`-Werten zurück, wenn der Host nicht analysiert werden kann.
     */
    public static getDomainParts(): {
        subdomain: string | null;
        domain: string | null;
        host: string | null;
        port: number | null;
        noSubdomain: string | null;
    } {
        const defaultParts: {
            subdomain: string | null;
            domain: string | null;
            host: string | null;
            port: number | null;
            noSubdomain: string | null;
        } = {
            subdomain: null,
            domain: null,
            host: null,
            port: null,
            noSubdomain: null,
        };

        const requestHost = window.location.host;

        if (!requestHost) {
            return defaultParts;
        }

        let host: string;
        let port: number | null;

        try {
            const url = new URL(`http://${requestHost}`);
            host = url.hostname;
            port = url.port ? parseInt(url.port, 10) : null;
        } catch (e) {
            return defaultParts;
        }

        let subdomain: string | null = null;
        let domain: string = host;

        // Regulärer Ausdruck zur Prüfung auf eine IPv4-Adresse.
        const isIpAddress = /^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$/.test(host);
        const hasDots = host.includes('.');

        // Subdomains werden nur für Hosts analysiert, die keine IP-Adresse sind und Punkte enthalten.
        if (!isIpAddress && hasDots) {
            const parts = host.split('.');
            const partCount = parts.length;
            const tld = parts[parts.length - 1];

            // Liste gängiger TLDs für die lokale Entwicklung
            const localTlds = ['localhost', 'local', 'test', 'dev'];
            const isLocalDomain = partCount > 1 && localTlds.includes(tld);

            if (isLocalDomain) {
                domain = tld;
                subdomain = parts.slice(0, -1).join('.');
            } else if (partCount > 2) {
                domain = parts.slice(-2).join('.');
                subdomain = parts.slice(0, -2).join('.');
            }
        }

        const noSubdomain = `${domain}${port ? `:${port}` : ''}`;

        return {
            subdomain,
            domain,
            host,
            port,
            noSubdomain,
        };
    }

    /**
     * Konvertiert ein JSON-Objekt-Array in eine CSV-Datei und startet den Download.
     * Alle Schlüssel des ersten Objekts werden als Spaltenüberschriften verwendet.
     *
     * @template T - Der Typ der Objekte im JSON-Array.
     * @param data - Das Array von JSON-Objekten, das in CSV konvertiert werden soll.
     * @param filename - Der Name der herunterzuladenden CSV-Datei (z.B. 'daten.csv').
     * @param delimiter - Das Trennzeichen für die CSV-Spalten (Standard: ';').
     */
    public static exportJsonToCsv<T extends Record<string, any>>(
        data: T[],
        filename: string,
        delimiter: string = ';'
    ): boolean {
        if (!data || data.length === 0) {
            console.warn('Keine Daten zum Exportieren vorhanden.');
            return false;
        }

        const columns = Object.keys(data[0]) as (keyof T)[];

        // Header-Zeile erstellen
        const header = columns.map(col => String(col)).join(delimiter);

        // Datenzeilen erstellen
        const rows = data.map(row => {
            return columns.map(col => {
                const value = row[col];
                let stringValue: string;

                if (value === null || value === undefined) {
                    stringValue = '';
                } else if (typeof value === 'string') {
                    // Anführungszeichen escapen und Wert in Anführungszeichen setzen, falls Trennzeichen oder Zeilenumbruch enthalten
                    stringValue = `"${value.replace(/"/g, '""')}"`;
                } else {
                    stringValue = String(value);
                }
                return stringValue;
            }).join(delimiter);
        });

        const csvString = [header, ...rows].join('\n');
        const blob = new Blob([csvString], {type: 'text/csv;charset=utf-8;'});

        // Download starten
        const link = document.createElement('a');
        if (link.download !== undefined) { // Nur für Browser, die den Download-Attribut unterstützen
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', filename);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            return true
        } else {
            return false;
        }
    }

    public static normalizeString(str: string): string {
        const characterMap: { [key: string]: string } = {
            'À': 'A', 'Á': 'A', 'Â': 'A', 'Ã': 'A', 'Ä': 'Ae', 'Å': 'A',
            'à': 'a', 'á': 'a', 'â': 'a', 'ã': 'a', 'ä': 'ae', 'å': 'a',
            'È': 'E', 'É': 'E', 'Ê': 'E', 'Ë': 'E',
            'è': 'e', 'é': 'e', 'ê': 'e', 'ë': 'e',
            'Ì': 'I', 'Í': 'I', 'Î': 'I', 'Ï': 'I',
            'ì': 'i', 'í': 'i', 'î': 'i', 'ï': 'i',
            'Ò': 'O', 'Ó': 'O', 'Ô': 'O', 'Õ': 'O', 'Ö': 'Oe',
            'ò': 'o', 'ó': 'o', 'ô': 'o', 'õ': 'o', 'ö': 'oe',
            'Ù': 'U', 'Ú': 'U', 'Û': 'U', 'Ü': 'Ue',
            'ù': 'u', 'ú': 'u', 'û': 'u', 'ü': 'ue',
            'ß': 'ss',
            'Ç': 'C', 'ç': 'c',
            'Ñ': 'N', 'ñ': 'n'
        };

        // Ersetze Sonderzeichen
        let normalized = str.replace(/[ÀÁÂÃÄÅàáâãäåÈÉÊËèéêëÌÍÎÏìíîïÒÓÔÕÖòóôõöÙÚÛÜùúûüßÇçÑñ]/g,
            (match) => characterMap[match]
        );

        // Entferne alle verbleibenden unsicheren Zeichen, erlaube aber mehr europäische Grundzeichen
        normalized = normalized.replace(/[^a-zA-Z0-9\-_ .,\n\t]/g, '');

        // Ersetze mehrere Leerzeichen durch eines
        return normalized.replace(/ {2,}/g, ' ').trim();
    }

    /**
     * Konvertiert Zeilenumbrüche in einem String basierend auf dem angegebenen Modus.
     * Nützlich, um Text für die Anzeige in Plaintext- oder HTML-Umgebungen vorzubereiten.
     *
     * @param text - Der Eingabetext, der Zeilenumbrüche enthalten kann.
     * @param mode - Der Konvertierungsmodus: 'plaintext' für `\r\n` oder 'html' für `<br/ >`.
     * @returns Der konvertierte String, bei dem Zeilenumbrüche ersetzt und führende/nachfolgende Leerzeichen entfernt wurden.
     */
    public static multilineText(text: string, mode: 'plaintext' | 'html' = 'plaintext'): string {
        text = text
            .replace(/ +/gim, ' ')
            .replace(/^([ \t])+/gim, '')
            .replace(/([ \t])+$/gim, '')
        ;

        if (mode === 'plaintext')
            text = text.replace(/\n/g, '\r\n');
        if (mode === 'html')
            text = text.replace(/\n/g, '<br/>');

        return text.trim();
    }

    /**
     * Sendet Diagnosedaten an lokalen Glitchtip-Sentry-Server ohne selbst Fehler zu verursachen.
     *
     * @param error - Exception die übermittelt werden soll
     */
    public static errorToSentry(error: unknown, enabled: boolean = true): void {
        if (enabled === false)
            return;

        try {
            // @ts-ignore
            Sentry.captureException(error);
        } catch {
            //
        }
    }

    /**
     * Verhindert häufige Aufrufe bei schnellen Ereignissen wie Fenster-Resizing oder Tastatureingaben.
     *
     * Erstellt eine debounced-Funktion, die `func` erst nach Ablauf von `delay`
     * Millisekunden aufruft, wenn sie nicht erneut aufgerufen wurde.
     *
     * @template T Funktionstyp mit beliebigen Argumenten
     * @param {T} func Zu debouncende Funktion
     * @param {number} delay Wartezeit in Millisekunden
     * @returns {(...args: Parameters<T>) => void} Debounced-Funktion
     */
    public static debounce<T extends (...args: any[]) => void>(
        func: T,
        delay: number
    ): (...args: Parameters<T>) => void {
        let timeoutId: ReturnType<typeof setTimeout> | null = null;

        return (...args: Parameters<T>): void => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }

            timeoutId = setTimeout(() => {
                func(...args);
            }, delay);
        };
    }

}
